// CreateCommunityDialog.tsx — functional Create Community form.
import { useState, useRef, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Dialog, Button, TextField, useToast } from '@/ui';
import { useCommunities, useRequireAuth } from '@/hooks';
import { mediaService, uploadErrorMessage } from '@/core/services/mediaService';
import { COVER_TONES, DEFAULT_CUSTOM_HUE, coverBackground, gradientForHue, hueToneValue, parseHueTone, toneLabel } from './meta';
import type { Community } from '@/core/models/community';

const Fields = styled.div`display: flex; flex-direction: column; gap: 12px; margin-bottom: 14px;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 5px;`;
const FieldLabel = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;
const Area = styled.textarea`
  width: 100%; box-sizing: border-box; min-height: 64px; resize: vertical;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md}; padding: 9px 11px;
  color: ${({ theme }) => theme.color.textPrimary}; font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm}; outline: none;
  &:focus-visible { border-color: ${({ theme }) => theme.color.accentCyan}; box-shadow: 0 0 0 3px ${({ theme }) => theme.color.focusRing}; }
`;
const Swatches = styled.div`display: flex; gap: 8px; margin: 6px 0 16px;`;
const Swatch = styled.button<{ $tone: string; $active: boolean }>`
  width: 34px; height: 34px; border-radius: ${({ theme }) => theme.radius.md}; cursor: pointer;
  background: ${({ $tone }) => coverBackground($tone)};
  border: 2px solid ${({ $active, theme }) => ($active ? theme.color.textPrimary : 'transparent')};
`;
const CoverRow = styled.div`display: flex; align-items: center; gap: 10px; margin: 6px 0 0;`;
const CustomRow = styled.div`display: flex; align-items: center; gap: 10px; margin: 12px 0 0;`;
const HueSlider = styled.input<{ $active: boolean }>`
  flex: 1; -webkit-appearance: none; appearance: none; height: 10px; border-radius: 999px;
  background: linear-gradient(to right,
    hsl(0, 70%, 45%), hsl(60, 70%, 45%), hsl(120, 70%, 45%), hsl(180, 70%, 45%),
    hsl(240, 70%, 45%), hsl(300, 70%, 45%), hsl(360, 70%, 45%));
  outline: none; cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.55)};
  &::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%;
    background: ${({ theme }) => theme.color.textPrimary};
    border: 2px solid ${({ theme }) => theme.color.bgRoot}; cursor: pointer;
  }
  &::-moz-range-thumb {
    width: 18px; height: 18px; border-radius: 50%;
    background: ${({ theme }) => theme.color.textPrimary};
    border: 2px solid ${({ theme }) => theme.color.bgRoot}; cursor: pointer;
  }
  &:focus-visible { box-shadow: 0 0 0 3px ${({ theme }) => theme.color.focusRing}; }
`;
// Background is set via inline style (not a styled prop) because it varies
// continuously as the slider drags — a styled-components class per hue tick
// would otherwise spam the stylesheet.
const CustomSwatch = styled.button<{ $active: boolean }>`
  width: 34px; height: 34px; border-radius: ${({ theme }) => theme.radius.md}; flex: 0 0 auto;
  cursor: pointer; padding: 0;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.color.textPrimary : 'transparent')};
`;
const UploadBtn = styled.button`
  background: none; border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md}; padding: 5px 12px;
  color: ${({ theme }) => theme.color.textMuted}; font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm}; cursor: pointer;
  &:hover { color: ${({ theme }) => theme.color.textPrimary}; background: ${({ theme }) => theme.color.hover}; }
`;
const CoverThumb = styled.img`
  width: 56px; height: 34px; object-fit: cover; border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const MediaErrMsg = styled.p`
  margin: 4px 0 0; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;
const Footer = styled.div`display: flex; justify-content: flex-end; gap: 8px;`;

export interface CreateCommunityDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (community: Community) => void;
}

export function CreateCommunityDialog({ open, onClose, onCreated }: CreateCommunityDialogProps) {
  const { addCommunity } = useCommunities();
  const requireAuth = useRequireAuth();
  const toast = useToast();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [tone, setTone] = useState<string>('a');
  // Slider's own position, independent of whether the custom hue is currently
  // the active selection — so moving it back and forth doesn't lose its place.
  const [customHue, setCustomHue] = useState<number>(DEFAULT_CUSTOM_HUE);
  const activeHue = parseHueTone(tone);
  const isCustomActive = activeHue !== null;
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [mediaErr, setMediaErr] = useState<string | null>(null);

  const coverPreviewUrl = useMemo(() => (coverFile ? URL.createObjectURL(coverFile) : null), [coverFile]);
  useEffect(() => () => { if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl); }, [coverPreviewUrl]);

  async function handleCreate() {
    if (!name.trim()) return;
    if (!requireAuth('create a community')) return;
    let coverImageKey: string | undefined;
    if (coverFile) {
      try {
        coverImageKey = (await mediaService.upload('cover', coverFile)).key;
      } catch (e) {
        setMediaErr(uploadErrorMessage(e, 'Could not upload cover. Please try again.'));
        return;
      }
    }
    const community = await addCommunity({
      name: name.trim(), category: category.trim() || 'General',
      tagline: tagline.trim(), description: description.trim(), coverTone: tone,
      ...(coverImageKey ? { coverImageKey } : {}),
    });
    toast(`Created ${community.name}`);
    onCreated(community);
  }

  return (
    <Dialog open={open} onClose={onClose} title="Create a community">
      <Fields>
        <TextField label="Community name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Indie Game Devs" />
        <TextField label="Category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Gaming" />
        <TextField label="Tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="A short rallying line" />
        <Field>
          <FieldLabel>Description</FieldLabel>
          <Area value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" placeholder="What is this community about?" />
        </Field>
      </Fields>
      <FieldLabel>Cover</FieldLabel>
      <Swatches role="radiogroup" aria-label="Cover color">
        {COVER_TONES.map((t) => (
          <Swatch key={t} type="button" $tone={t} $active={tone === t} role="radio" aria-checked={tone === t} aria-label={`Cover tone: ${toneLabel(t)}`} onClick={() => { setTone(t); setCoverFile(null); if (coverInputRef.current) coverInputRef.current.value = ''; }} />
        ))}
        {/* Seventh radiogroup option — without this, activating the custom hue
            leaves the group reporting zero checked radios to assistive tech. */}
        <CustomSwatch
          type="button"
          role="radio"
          aria-checked={isCustomActive}
          aria-label="Custom colour"
          $active={isCustomActive}
          style={{ background: gradientForHue(customHue) }}
          onClick={() => {
            setTone(hueToneValue(customHue));
            setCoverFile(null);
            if (coverInputRef.current) coverInputRef.current.value = '';
          }}
        />
      </Swatches>
      <CustomRow>
        <label htmlFor="cover-hue-slider" style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
          <FieldLabel>Custom colour</FieldLabel>
          <HueSlider
            id="cover-hue-slider"
            type="range"
            min={0}
            max={359}
            step={1}
            value={customHue}
            $active={isCustomActive}
            aria-valuetext={`Hue ${customHue}`}
            onChange={(e) => {
              const h = Number(e.target.value);
              setCustomHue(h);
              setTone(hueToneValue(h));
              setCoverFile(null);
              if (coverInputRef.current) coverInputRef.current.value = '';
            }}
          />
        </label>
      </CustomRow>
      <CoverRow>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          data-testid="create-cover-input"
          hidden
          onChange={(e) => { setMediaErr(null); setCoverFile(e.target.files?.[0] ?? null); }}
        />
        <UploadBtn type="button" onClick={() => coverInputRef.current?.click()}>
          {coverFile ? 'Change cover image' : 'Upload cover image'}
        </UploadBtn>
        {coverFile && coverPreviewUrl && (
          <CoverThumb
            src={coverPreviewUrl}
            alt="Cover preview"
          />
        )}
      </CoverRow>
      {mediaErr && <MediaErrMsg role="alert">{mediaErr}</MediaErrMsg>}
      <Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!name.trim()} onClick={() => void handleCreate()}>Create community</Button>
      </Footer>
    </Dialog>
  );
}
